import androidx.compose.runtime.*
import or.app.orientalrecepies.network.models.Category
import or.app.orientalrecepies.network.models.Recipe
import or.app.orientalrecepies.network.models.ResultState
import or.app.orientalrecepies.network.repository.RecipeService
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.window
import kotlinx.browser.document

sealed class FrontendScreen {
    data object Home : FrontendScreen()
    data class Search(val query: String = "", val category: String? = null) : FrontendScreen()
    data object Favorites : FrontendScreen()
    data object FridgeChef : FrontendScreen()
    data class RecipeDetails(val recipeId: String) : FrontendScreen()
    data object Categories : FrontendScreen()
}

@Composable
fun RecipeApp() {
    var screenStack by remember { mutableStateOf<List<FrontendScreen>>(listOf(FrontendScreen.Home)) }
    val currentScreen = screenStack.last()

    // Unikatan deviceId za web (koristimo localStorage ako je moguće)
    val deviceId = remember {
        val storage = window.localStorage
        val existing = storage.getItem("device_id")
        if (existing != null) {
            existing
        } else {
            val newId = (1..16).map { "abcdefghijklmnopqrstuvwxyz0123456789"[kotlin.random.Random.nextInt(36)] }.joinToString("")
            storage.setItem("device_id", newId)
            newId
        }
    }

    fun navigateTo(screen: FrontendScreen) {
        screenStack = screenStack + screen
    }

    fun navigateBack() {
        if (screenStack.size > 1) {
            screenStack = screenStack.dropLast(1)
        }
    }

    Div(attrs = { classes("h-screen", "bg-nano-background", "text-white", "flex", "flex-col", "no-scrollbar") }) {
        Navbar(
            currentScreen = currentScreen,
            onNavigate = { navigateTo(it) }
        )

        Main(attrs = { classes("flex-1", "overflow-y-auto", "no-scrollbar") }) {
            when (val screen = currentScreen) {
                is FrontendScreen.Home -> HomeScreen(
                    onRecipeClick = { navigateTo(FrontendScreen.RecipeDetails(it)) },
                    onCategoryClick = { navigateTo(FrontendScreen.Search(category = it)) },
                    onSeeAllCategories = { navigateTo(FrontendScreen.Categories) }
                )
                is FrontendScreen.Search -> SearchPage(
                    initialQuery = screen.query,
                    initialCategory = screen.category,
                    onRecipeClick = { navigateTo(FrontendScreen.RecipeDetails(it)) }
                )
                is FrontendScreen.Favorites -> FavoritesPage(
                    deviceId = deviceId,
                    onRecipeClick = { navigateTo(FrontendScreen.RecipeDetails(it)) }
                )
                is FrontendScreen.FridgeChef -> FridgeChefPage(
                    onRecipeClick = { navigateTo(FrontendScreen.RecipeDetails(it)) }
                )
                is FrontendScreen.RecipeDetails -> PublicRecipeDetailsPage(
                    recipeId = screen.recipeId,
                    onBack = { navigateBack() }
                )
                is FrontendScreen.Categories -> CategoriesPage(
                    onCategoryClick = { navigateTo(FrontendScreen.Search(category = it)) },
                    onBack = { navigateBack() }
                )
            }
            Footer()
        }
    }
}

@Composable
fun Navbar(currentScreen: FrontendScreen, onNavigate: (FrontendScreen) -> Unit) {
    Nav(attrs = { classes("bg-nano-surface/80", "backdrop-blur-md", "sticky", "top-0", "z-50", "border-b", "border-white/5") }) {
        Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "sm:px-6", "lg:px-8", "h-20", "flex", "items-center", "justify-between") }) {
            // Logo
            Div(attrs = {
                classes("flex", "items-center", "gap-3", "cursor-pointer")
                onClick { onNavigate(FrontendScreen.Home) }
            }) {
                Img(src = "favicon.png", attrs = { classes("w-8", "h-8", "sm:w-10", "sm:h-10", "rounded-xl") })
                H1(attrs = { classes("hidden", "lg:block", "text-2xl", "font-black", "tracking-tighter") }) { Text("ŠAFRAN") }
            }

            // Desktop & Mobile Links (Unified as Buttons)
            Div(attrs = { classes("flex", "items-center", "gap-2", "sm:gap-4") }) {
                NavButton("Početna", "home", currentScreen is FrontendScreen.Home) { onNavigate(FrontendScreen.Home) }
                NavButton("Istraži", "search", currentScreen is FrontendScreen.Search) { onNavigate(FrontendScreen.Search()) }
                NavButton("Favoriti", "favorite", currentScreen is FrontendScreen.Favorites) { onNavigate(FrontendScreen.Favorites) }
                NavButton("Frižider", "kitchen", currentScreen is FrontendScreen.FridgeChef) { onNavigate(FrontendScreen.FridgeChef) }

                Div(attrs = { classes("hidden", "lg:block", "w-px", "h-8", "bg-white/10", "mx-2") })

                Button(attrs = {
                    classes("hidden", "lg:block", "bg-nano-primary", "text-black", "px-6", "py-2.5", "rounded-xl", "font-bold", "hover:scale-105", "transition-all")
                    onClick { /* Link to mobile app or something */ }
                }) {
                    Text("Preuzmi App")
                }
            }
        }
    }
}

@Composable
fun NavButton(text: String, icon: String, isActive: Boolean, onClick: () -> Unit) {
    Button(attrs = {
        classes("flex", "items-center", "gap-2", "px-3", "py-2.5", "sm:px-5", "rounded-2xl", "transition-all", "font-black", "text-[10px]", "uppercase", "tracking-tighter")
        if (isActive) {
            classes("bg-nano-primary", "text-black", "shadow-xl", "shadow-nano-primary/20", "scale-105")
        } else {
            classes("text-gray-400", "hover:text-white", "hover:bg-white/5")
        }
        onClick { onClick() }
    }) {
        MaterialIcon(icon, if (isActive) "" else "text-gray-500 opacity-50")
        Span(attrs = { classes("hidden", "md:block") }) { Text(text) }
    }
}

@Composable
fun HomeScreen(onRecipeClick: (String) -> Unit, onCategoryClick: (String) -> Unit, onSeeAllCategories: () -> Unit) {
    var categories by remember { mutableStateOf<List<Category>?>(null) }
    var promotedRecipes by remember { mutableStateOf<List<Recipe>?>(null) }
    var popularRecipes by remember { mutableStateOf<List<Recipe>?>(null) }

    LaunchedEffect(Unit) {
        RecipeService.getCategories { if (it is ResultState.Success) categories = it.data }
        RecipeService.getPromotedRecipes { if (it is ResultState.Success) promotedRecipes = it.data }
        RecipeService.getRecipes(activeOnly = true) { if (it is ResultState.Success) popularRecipes = it.data.take(6) }
    }

    Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "py-12", "space-y-16") }) {
        // Hero / Promoted
        if (promotedRecipes == null) {
            Div(attrs = { classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-6") }) {
                repeat(3) {
                    SkeletonLoader(listOf("h-[300px]", "rounded-[32px]"))
                }
            }
        } else if (promotedRecipes!!.isNotEmpty()) {
            Div(attrs = { classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-6") }) {
                promotedRecipes!!.take(3).forEach { promoted ->
                    Div(attrs = {
                        classes("relative", "h-[300px]", "rounded-[32px]", "overflow-hidden", "group", "cursor-pointer")
                        onClick { onRecipeClick(promoted.id) }
                    }) {
                        Img(src = getFullImageUrl(promoted.imageUrl), attrs = { classes("w-full", "h-full", "object-cover", "group-hover:scale-105", "transition-transform", "duration-700") })
                        Div(attrs = { classes("absolute", "inset-0", "bg-gradient-to-t", "from-black", "via-black/20", "to-transparent") })
                        Div(attrs = { classes("absolute", "bottom-8", "left-8", "right-8") }) {
                            Span(attrs = { classes("bg-nano-primary", "text-black", "px-3", "py-1", "rounded-full", "text-[10px]", "font-black", "uppercase", "mb-3", "inline-block") }) {
                                Text("Istaknuto")
                            }
                            H2(attrs = { classes("text-2xl", "font-black", "mb-2", "line-clamp-1") }) { Text(promoted.title) }
                            P(attrs = { classes("text-gray-300", "text-sm", "line-clamp-2") }) { Text(promoted.description) }
                        }
                    }
                }
            }
        }

        // Categories
        Section("Kategorije", "Vidi sve", onActionClick = onSeeAllCategories) {
            if (categories == null) {
                CategorySkeletonRow()
            } else {
                Div(attrs = { classes("flex", "gap-4", "overflow-x-auto", "no-scrollbar", "pb-4") }) {
                    categories!!.forEach { category ->
                        Div(attrs = {
                            classes("bg-nano-surface", "px-8", "py-4", "rounded-2xl", "whitespace-nowrap", "hover:bg-nano-primary/10", "hover:border-nano-primary/30", "border", "border-white/5", "transition-all", "cursor-pointer")
                            onClick { onCategoryClick(category.name) }
                        }) {
                            Div(attrs = { classes("font-bold") }) { Text(category.name) }
                        }
                    }
                }
            }
        }

        // Popular
        Section("Najnoviji recepti", null) {
            if (popularRecipes == null) {
                RecipeSkeletonGrid()
            } else {
                Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-8") }) {
                    popularRecipes!!.forEach { recipe ->
                        PublicRecipeCard(recipe) { onRecipeClick(it) }
                    }
                }
            }
        }
    }
}

@Composable
fun Section(title: String, actionText: String?, onActionClick: (() -> Unit)? = null, content: @Composable () -> Unit) {
    Div {
        Div(attrs = { classes("flex", "justify-between", "items-end", "mb-8") }) {
            Div {
                H2(attrs = { classes("text-3xl", "font-black") }) { Text(title) }
                Div(attrs = { classes("w-12", "h-1.5", "bg-nano-primary", "rounded-full", "mt-2") })
            }
            if (actionText != null) {
                A(href = "#", attrs = {
                    classes("text-nano-primary", "font-bold", "hover:underline")
                    onClick { it.preventDefault(); onActionClick?.invoke() }
                }) { Text(actionText) }
            }
        }
        content()
    }
}

@Composable
fun PublicRecipeCard(recipe: Recipe, onClick: (String) -> Unit) {
    Div(attrs = {
        classes("bg-nano-surface", "rounded-[32px]", "overflow-hidden", "border", "border-white/5", "hover:border-white/10", "transition-all", "group", "cursor-pointer")
        onClick { onClick(recipe.id) }
    }) {
        Div(attrs = { classes("relative", "h-64") }) {
            Img(src = getFullImageUrl(recipe.imageUrl), attrs = { classes("w-full", "h-full", "object-cover", "group-hover:scale-105", "transition-transform") })
            Div(attrs = { classes("absolute", "top-4", "right-4", "bg-black/50", "backdrop-blur-md", "px-3", "py-1.5", "rounded-2xl", "flex", "items-center", "gap-1") }) {
                MaterialIcon("star", "text-nano-primary text-sm")
                Span(attrs = { classes("text-xs", "font-bold") }) { Text(recipe.rating.toString()) }
            }
        }
        Div(attrs = { classes("p-6") }) {
            Span(attrs = { classes("text-[10px]", "font-black", "uppercase", "tracking-widest", "text-nano-primary") }) { Text(recipe.category) }
            H3(attrs = { classes("text-xl", "font-bold", "mt-1", "mb-3", "line-clamp-1") }) { Text(recipe.title) }

            Div(attrs = { classes("flex", "items-center", "gap-4", "text-gray-400", "text-sm") }) {
                Div(attrs = { classes("flex", "items-center", "gap-1") }) {
                    MaterialIcon("schedule", "text-xs")
                    Text(recipe.prepTime)
                }
                Div(attrs = { classes("flex", "items-center", "gap-1") }) {
                    MaterialIcon("bar_chart", "text-xs")
                    Text(recipe.level)
                }
            }
        }
    }
}

@Composable
fun SearchPage(initialQuery: String, initialCategory: String?, onRecipeClick: (String) -> Unit) {
    var query by remember { mutableStateOf(initialQuery) }
    var recipes by remember { mutableStateOf<List<Recipe>?>(null) }
    var isLoading by remember { mutableStateOf(false) }

    fun performSearch() {
        isLoading = true
        RecipeService.getRecipes(searchText = query, category = initialCategory) {
            isLoading = false
            if (it is ResultState.Success) recipes = it.data
        }
    }

    LaunchedEffect(query, initialCategory) {
        performSearch()
    }

    Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "py-12") }) {
        H2(attrs = { classes("text-4xl", "font-black", "mb-8") }) {
            Text(if (initialCategory != null) "Kategorija: $initialCategory" else "Pretraga")
        }

        // Search Bar
        Div(attrs = { classes("relative", "max-w-2xl", "mb-12") }) {
            Input(type = InputType.Text, attrs = {
                classes("w-full", "bg-nano-surface", "border", "border-white/10", "rounded-2xl", "p-5", "pl-14", "text-white", "focus:border-nano-primary", "outline-none", "text-lg")
                placeholder("Šta vam se danas jede?")
                value(query)
                onInput { query = it.value }
            })
            Div(attrs = { classes("absolute", "left-5", "top-1/2", "-translate-y-1/2") }) {
                MaterialIcon("search", "text-gray-400 text-2xl")
            }
        }

        if (isLoading || recipes == null) {
            SearchSkeletonGrid()
        } else if (recipes!!.isEmpty()) {
            Div(attrs = { classes("text-center", "py-20", "text-gray-500") }) {
                MaterialIcon("search_off", "text-6xl mb-4")
                P { Text("Nismo pronašli nijedan recept za vašu pretragu.") }
            }
        } else {
            Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-8") }) {
                recipes!!.forEach { PublicRecipeCard(it, onRecipeClick) }
            }
        }
    }
}

@Composable
fun PublicRecipeDetailsPage(recipeId: String, onBack: () -> Unit) {
    var recipe by remember { mutableStateOf<Recipe?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var isFavorite by remember { mutableStateOf(false) }
    var isTogglingFavorite by remember { mutableStateOf(false) }

    // Unikatan deviceId za web
    val deviceId = remember { window.localStorage.getItem("device_id") ?: "web_user" }

    LaunchedEffect(recipeId) {
        isLoading = true
        recipe = null

        kotlinx.coroutines.delay(1000)

        RecipeService.getRecipeById(recipeId) { recipeResult ->
            if (recipeResult is ResultState.Success) {
                val fetchedRecipe = recipeResult.data
                document.title = "Šafran - ${fetchedRecipe.title}"

                RecipeService.getFavourites(deviceId) { favResult ->
                    if (favResult is ResultState.Success) {
                        isFavorite = favResult.data.any { fav -> fav.id == recipeId }
                    }
                    recipe = fetchedRecipe
                    window.setTimeout({ isLoading = false }, 50)
                }
            } else {
                isLoading = false
            }
        }
    }

    if (isLoading || recipe == null) {
        RecipeDetailsSkeleton()
    } else {
        recipe?.let { r ->
            Div(attrs = { classes("max-w-5xl", "mx-auto", "px-4", "py-12") }) {
                // Header with Back and Like
                Div(attrs = { classes("flex", "justify-between", "items-center", "mb-8") }) {
                    // Back Button
                    Button(attrs = {
                        classes("flex", "items-center", "gap-2", "text-gray-400", "hover:text-white", "transition-colors", "bg-white/5", "px-4", "py-2", "rounded-xl")
                        onClick { onBack() }
                    }) {
                        MaterialIcon("arrow_back")
                        Span { Text("Nazad") }
                    }

                    // Like Button
                    Button(attrs = {
                        val currentToggleState = isTogglingFavorite
                        val currentFavState = isFavorite
                        classes("flex", "items-center", "justify-center", "w-12", "h-12", "rounded-xl", "transition-all", "relative")
                        if (currentFavState) classes("bg-red-500/10", "text-red-500") else classes("bg-white/5", "text-gray-400", "hover:text-white")
                        if (currentToggleState) classes("opacity-50", "cursor-wait")

                        onClick {
                            if (isTogglingFavorite) return@onClick

                            isTogglingFavorite = true
                            RecipeService.toggleFavorite(deviceId, r.id, !isFavorite) {
                                isTogglingFavorite = false
                                if (it is ResultState.Success) {
                                    isFavorite = !isFavorite
                                }
                            }
                        }
                    }) {
                        if (isTogglingFavorite) {
                            Div(attrs = { classes("w-5", "h-5", "border-2", "border-current", "border-t-transparent", "rounded-full", "animate-spin", "absolute", "inset-0", "m-auto") })
                        } else {
                            MaterialIcon(if (isFavorite) "favorite" else "favorite_border")
                        }
                    }
                }

                // Hero section sa slikom
                Div(attrs = {
                    classes("relative", "h-64", "sm:h-[400px]", "w-full", "rounded-3xl", "overflow-hidden", "shadow-2xl", "shadow-black", "mb-8", "sm:mb-12")
                }) {
                    Img(src = getFullImageUrl(r.imageUrl), attrs = { classes("w-full", "h-full", "object-cover") })
                    Div(attrs = {
                        classes("absolute", "inset-0", "bg-gradient-to-t", "from-black/80", "via-black/20", "to-transparent")
                    })

                    Div(attrs = { classes("absolute", "bottom-8", "left-8", "right-8") }) {
                        Span(attrs = {
                            classes("inline-block", "px-4", "py-2", "bg-nano-primary", "text-black", "text-xs", "font-black", "rounded-full", "uppercase", "mb-4")
                        }) {
                            Text(r.category)
                        }
                        H2(attrs = { classes("text-3xl", "sm:text-5xl", "font-black", "text-white") }) {
                            Text(r.title)
                        }
                    }
                }

                // Info Grid
                Div(attrs = {
                    classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-4", "sm:gap-8", "mb-12")
                }) {
                    DetailCard("schedule", "Spremanje", r.prepTime, "text-blue-400")
                    DetailCard("bar_chart", "Težina", r.level, "text-nano-olive")
                    DetailCard("star", "Ocjena", "${r.rating} / 5.0", "text-nano-primary")
                }

                // Description
                Div(attrs = {
                    classes("bg-nano-surface", "p-6", "sm:p-8", "rounded-3xl", "border", "border-white/5", "mb-12")
                }) {
                    H3(attrs = {
                        classes("text-xl", "font-bold", "mb-4", "text-gray-500")
                    }) { Text("Opis recepta") }
                    P(attrs = {
                        classes("text-lg", "sm:text-xl", "text-gray-200", "leading-relaxed")
                    }) { Text(r.description) }
                }

                // Sastojci i Priprema
                Div(attrs = {
                    classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-8", "sm:gap-12")
                }) {
                    // Sastojci
                    Div(attrs = {
                        classes("bg-nano-surface/50", "p-8", "rounded-3xl", "border", "border-white/5")
                    }) {
                        H3(attrs = {
                            classes("text-2xl", "font-bold", "mb-8", "flex", "items-center", "gap-3")
                        }) {
                            MaterialIcon("shopping_basket", "text-nano-primary text-3xl")
                            Text("Sastojci")
                        }
                        Ul(attrs = { classes("space-y-4") }) {
                            r.ingredients.forEach { ingredient ->
                                Li(attrs = {
                                    classes("flex", "justify-between", "items-center", "p-4", "bg-white/5", "rounded-2xl", "border", "border-white/5")
                                }) {
                                    Span(attrs = { classes("text-gray-300", "font-medium") }) { Text(ingredient.name) }
                                    Span(attrs = {
                                        classes("px-3", "py-1", "bg-nano-primary/10", "text-nano-primary", "font-black", "rounded-lg")
                                    }) { Text(ingredient.amount) }
                                }
                            }
                        }
                    }

                    // Priprema
                    Div(attrs = {
                        classes("bg-nano-surface/50", "p-8", "rounded-3xl", "border", "border-white/5")
                    }) {
                        H3(attrs = {
                            classes("text-2xl", "font-bold", "mb-8", "flex", "items-center", "gap-3")
                        }) {
                            MaterialIcon("soup_kitchen", "text-nano-primary text-3xl")
                            Text("Priprema")
                        }
                        Div(attrs = { classes("space-y-8") }) {
                            r.instructions.forEach { step ->
                                Div(attrs = { classes("flex", "gap-6") }) {
                                    Div(attrs = {
                                        classes("flex-shrink-0", "w-10", "h-10", "bg-nano-primary", "text-black", "font-black", "rounded-xl", "flex", "items-center", "justify-center", "shadow-lg", "shadow-nano-primary/20", "rotate-3")
                                    }) {
                                        Text(step.step.toString())
                                    }
                                    P(attrs = {
                                        classes("text-gray-300", "leading-relaxed", "text-lg")
                                    }) { Text(step.description) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun Footer() {
    Footer(attrs = { classes("bg-nano-surface", "py-12", "mt-24", "border-t", "border-white/5") }) {
        Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "text-center") }) {
            Div(attrs = { classes("flex", "items-center", "justify-center", "gap-3", "mb-6") }) {
                Img(src = "favicon.png", attrs = { classes("w-8", "h-8", "rounded-lg") })
                Span(attrs = { classes("text-xl", "font-black") }) { Text("ŠAFRAN") }
            }
            P(attrs = { classes("text-gray-500", "text-sm") }) {
                Text("© 2024 Nano Banani. Sva prava zadržana. Najbolji recepti na jednom mjestu.")
            }
        }
    }
}

@Composable
fun FavoritesPage(deviceId: String, onRecipeClick: (String) -> Unit) {
    var recipes by remember { mutableStateOf<List<Recipe>?>(null) }
    var searchQuery by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(true) }

    fun loadFavorites() {
        isLoading = true
        RecipeService.getFavourites(deviceId) { result ->
            isLoading = false
            if (result is ResultState.Success) {
                recipes = result.data
            }
        }
    }

    LaunchedEffect(Unit) {
        loadFavorites()
    }

    val filteredRecipes = recipes?.filter {
        it.title.contains(searchQuery, ignoreCase = true) || it.description.contains(searchQuery, ignoreCase = true)
    }

    Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "py-12") }) {
        H2(attrs = { classes("text-4xl", "font-black", "mb-12") }) { Text("Moji Favoriti") }

        // Search Bar
        Div(attrs = { classes("relative", "max-w-2xl", "mb-12") }) {
            Input(type = InputType.Text, attrs = {
                classes("w-full", "bg-nano-surface", "border", "border-white/10", "rounded-2xl", "p-5", "pl-14", "text-white", "focus:border-nano-primary", "outline-none", "text-lg")
                placeholder("Pretraži favorite...")
                value(searchQuery)
                onInput { searchQuery = it.value }
            })
            Div(attrs = { classes("absolute", "left-5", "top-1/2", "-translate-y-1/2") }) {
                MaterialIcon("search", "text-gray-400 text-2xl")
            }
        }

        if (isLoading || recipes == null) {
            SearchSkeletonGrid()
        } else if (filteredRecipes!!.isEmpty()) {
            Div(attrs = { classes("text-center", "py-20", "text-gray-500", "bg-nano-surface/20", "rounded-[40px]", "border", "border-dashed", "border-white/10") }) {
                MaterialIcon("star_border", "text-6xl mb-4")
                P { Text(if (searchQuery.isEmpty()) "Još uvijek nemate omiljenih recepata." else "Nema rezultata za vašu pretragu.") }
            }
        } else {
            Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "lg:grid-cols-3", "gap-8") }) {
                filteredRecipes!!.forEach { PublicRecipeCard(it, onRecipeClick) }
            }
        }
    }
}

@Composable
fun FridgeChefPage(onRecipeClick: (String) -> Unit) {
    val selectedIngredients = remember { mutableStateListOf<String>() }
    var ingredientInput by remember { mutableStateOf("") }
    var isGenerating by remember { mutableStateOf(false) }
    var generatedRecipe by remember { mutableStateOf<Recipe?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "py-12") }) {
        Div(attrs = { classes("max-w-3xl", "mb-12") }) {
            H2(attrs = { classes("text-4xl", "font-black", "mb-4") }) { Text("Šta imate u frižideru?") }
            P(attrs = { classes("text-gray-400", "text-lg") }) {
                Text("Dodajte sastojke koje imate i dopustite našem AI kuharu da vam predloži savršen recept.")
            }
        }

        // Layout grid context
        Div(attrs = { classes("grid", "grid-cols-1", "lg:grid-cols-2", "gap-12", "items-start") }) {
            // Input section
            Div {
                Div(attrs = { classes("bg-nano-surface", "p-8", "rounded-[40px]", "border", "border-white/5") }) {
                    Div(attrs = { classes("flex", "gap-4", "mb-8") }) {
                        Div(attrs = { classes("relative", "flex-1") }) {
                            Input(type = InputType.Text, attrs = {
                                classes("w-full", "bg-white/5", "border", "border-white/10", "rounded-2xl", "p-5", "pl-14", "text-white", "focus:border-nano-primary", "outline-none")
                                placeholder("npr. Piletina, Riža, Paradajz...")
                                value(ingredientInput)
                                onInput { ingredientInput = it.value }
                                onKeyDown {
                                    if (it.key == "Enter" && ingredientInput.isNotBlank()) {
                                        if (!selectedIngredients.contains(ingredientInput)) {
                                            selectedIngredients.add(ingredientInput.trim())
                                        }
                                        ingredientInput = ""
                                    }
                                }
                            })
                            Div(attrs = { classes("absolute", "left-5", "top-1/2", "-translate-y-1/2") }) {
                                MaterialIcon("kitchen", "text-gray-500")
                            }
                        }
                        Button(attrs = {
                            classes("bg-white/5", "text-white", "px-8", "rounded-2xl", "font-bold", "hover:bg-white/10", "transition-colors")
                            onClick {
                                if (ingredientInput.isNotBlank()) {
                                    if (!selectedIngredients.contains(ingredientInput)) {
                                        selectedIngredients.add(ingredientInput.trim())
                                    }
                                    ingredientInput = ""
                                }
                            }
                        }) { Text("Dodaj") }
                    }

                    // Tags
                    if (selectedIngredients.isNotEmpty()) {
                        Div(attrs = { classes("flex", "flex-wrap", "gap-3") }) {
                            selectedIngredients.forEach { ingredient ->
                                Div(attrs = { classes("bg-nano-primary/10", "text-nano-primary", "px-4", "py-2", "rounded-xl", "flex", "items-center", "gap-2", "border", "border-nano-primary/20", "font-bold") }) {
                                    Text(ingredient)
                                    Button(attrs = {
                                        classes("hover:text-white", "transition-colors")
                                        onClick { selectedIngredients.remove(ingredient) }
                                    }) { MaterialIcon("close", "text-sm") }
                                }
                            }
                        }
                    } else {
                        P(attrs = { classes("text-gray-500", "text-center", "py-4", "italic") }) {
                            Text("Vaša lista sastojaka je prazna.")
                        }
                    }
                }

                // Action Button
                Button(attrs = {
                    classes("w-full", "py-6", "rounded-3xl", "text-xl", "font-black", "transition-all", "flex", "items-center", "justify-center", "gap-3", "mt-8")
                    if (selectedIngredients.isEmpty() || isGenerating) {
                        classes("bg-gray-800", "text-gray-500", "cursor-not-allowed")
                    } else {
                        classes("bg-nano-primary", "text-black", "hover:scale-[1.02]", "shadow-2xl", "shadow-nano-primary/20")
                    }
                    onClick {
                        if (selectedIngredients.isNotEmpty() && !isGenerating) {
                            isGenerating = true
                            errorMessage = null
                            generatedRecipe = null
                            RecipeService.generateAiRecipe(selectedIngredients.toList()) { result ->
                                isGenerating = false
                                if (result is ResultState.Success<Recipe>) {
                                    generatedRecipe = result.data
                                } else if (result is ResultState.Error) {
                                    errorMessage = result.message ?: "Došlo je do greške pri generisanju recepta."
                                }
                            }
                        }
                    }
                }) {
                    if (isGenerating) {
                        Div(attrs = { classes("w-6", "h-6", "border-2", "border-black", "border-t-transparent", "rounded-full", "animate-spin") }) {}
                        Text("AI KUHA VAŠ RECEPT...")
                    } else {
                        MaterialIcon("auto_awesome")
                        Text("GENERIŠI RECEPT")
                    }
                }

                if (errorMessage != null) {
                    Div(attrs = { classes("mt-6", "p-4", "bg-red-500/10", "border", "border-red-500/30", "rounded-2xl", "text-red-500", "text-center") }) {
                        Text(errorMessage!!)
                    }
                }
            }

            // Results Section
            Div {
                if (generatedRecipe != null) {
                    Div(attrs = { classes("space-y-6", "animate-in", "fade-in", "slide-in-from-bottom-4", "duration-500") }) {
                        H3(attrs = { classes("text-2xl", "font-black", "text-nano-primary") }) { Text("Prijedlog AI Kuhara:") }
                        PublicRecipeCard(generatedRecipe!!, onRecipeClick)
                        P(attrs = { classes("text-gray-500", "text-sm", "text-center") }) {
                            Text("Kliknite na karticu iznad da otvorite cijeli recept.")
                        }
                    }
                } else {
                    // Placeholder kartica sa FORSIRANIM pulsiranjem i presijavanjem
                    Div(attrs = {
                        classes("h-[450px]", "flex", "flex-col", "items-center", "justify-center", "rounded-[40px]", "p-12", "transition-all", "duration-500", "relative", "overflow-hidden")

                        if (isGenerating) {
                            // Ekstremno uočljivo pulsiranje uz primarnu boju
                            classes("animate-pulse", "bg-nano-primary/20", "border-4", "border-solid", "border-nano-primary", "shadow-[0_0_50px_rgba(var(--nano-primary-rgb),0.3)]")
                        } else {
                            classes("bg-nano-surface/20", "border-2", "border-dashed", "border-white/10", "text-gray-600")
                        }
                    }) {
                        // Moćni shimmer efekat (presijavanje)
                        if (isGenerating) {
                            Div(attrs = {
                                classes(
                                    "absolute", "inset-0",
                                    "-translate-x-full",
                                    "animate-shimmer",
                                    "bg-gradient-to-r",
                                    "from-transparent",
                                    "via-white/20",
                                    "to-transparent"
                                )
                                // Ponavljamo shimmer da bude gušći
                                style {
                                    property("animation-duration", "1.5s")
                                }
                            })
                        }

                        if (isGenerating) {
                            // Sadržaj dok AI radi
                            Div(attrs = { classes("relative", "z-10", "flex", "flex-col", "items-center", "animate-bounce") }) {
                                Div(attrs = { classes("w-24", "h-24", "border-8", "border-nano-primary", "border-t-transparent", "rounded-full", "animate-spin", "mb-10") })
                                H3(attrs = { classes("text-4xl", "font-black", "text-white", "text-center", "tracking-tighter") }) {
                                    Text("AI KUHAR RADI...")
                                }
                                P(attrs = { classes("text-white/80", "mt-4", "font-black", "uppercase", "text-sm", "tracking-widest") }) {
                                    Text("Pripremam recept")
                                }
                            }
                        } else {
                            // Početno stanje
                            MaterialIcon("auto_awesome", "text-7xl mb-6 opacity-20")
                            H3(attrs = { classes("text-center", "font-black", "text-2xl", "opacity-40") }) {
                                Text("Rezultat generisanja")
                            }
                            P(attrs = { classes("text-center", "font-medium", "text-lg", "opacity-30", "mt-2") }) {
                                Text("Pojavit će se ovdje nakon što kliknete dugme.")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun CategoriesPage(onCategoryClick: (String) -> Unit, onBack: () -> Unit) {
    var categories by remember { mutableStateOf<List<Category>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(Unit) {
        isLoading = true
        kotlinx.coroutines.delay(800) // Minimum visible delay for shimmer stability
        RecipeService.getCategories {
            if (it is ResultState.Success) {
                categories = it.data
                window.setTimeout({ isLoading = false }, 50)
            } else {
                isLoading = false
            }
        }
    }

    Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "py-12") }) {
        // Back Button
        Button(attrs = {
            classes("flex", "items-center", "gap-2", "text-gray-400", "mb-8", "hover:text-white", "transition-colors")
            onClick { onBack() }
        }) {
            MaterialIcon("arrow_back")
            Span { Text("Nazad") }
        }

        H2(attrs = { classes("text-4xl", "font-black", "mb-12") }) { Text("Sve Kategorije") }

        if (isLoading || categories.isEmpty()) {
            Div(attrs = { classes("grid", "grid-cols-2", "md:grid-cols-3", "lg:grid-cols-4", "gap-4", "sm:gap-6") }) {
                repeat(12) {
                    LargeCategorySkeletonItem()
                }
            }
        } else {
            Div(attrs = { classes("grid", "grid-cols-2", "md:grid-cols-3", "lg:grid-cols-4", "gap-4", "sm:gap-6") }) {
                categories.forEach { category ->
                    Div(attrs = {
                        classes("bg-nano-surface", "p-4", "sm:p-10", "rounded-2xl", "sm:rounded-[32px]", "text-center", "hover:bg-nano-primary/10", "hover:border-nano-primary/30", "border", "border-white/5", "transition-all", "cursor-pointer", "group")
                        onClick { onCategoryClick(category.name) }
                    }) {
                        Div(attrs = { classes("text-base", "sm:text-2xl", "font-black", "group-hover:text-nano-primary", "transition-colors", "break-words") }) {
                            Text(category.name)
                        }
                    }
                }
            }
        }
    }
}

// SkeletonLoader and RecipeSkeletonItem are now imported from WebComponents.kt
